// src/utils/contractScopeHelpers.js
// Data model, default templates, and helper selectors for Contracts & Scope

// ============================================================================
// INTERIOR DELIVERABLES SECTIONS
// ============================================================================
// The Summary of Interior Deliverables is split into two sections so a scope
// can include just the Warm Shell items, just the FF&E items, or both. Each
// item carries a `section` field; the scope itself carries two toggles
// (`interiorWarmShellSelected`, `interiorFfeSelected`) that decide which
// section(s) render in the PDF.

export const INTERIOR_SECTIONS = {
  WARM_SHELL: 'warm-shell',
  FFE: 'ffe',
};

export const INTERIOR_SECTION_LABELS = {
  'warm-shell': 'Warm Shell',
  'ffe': 'FF&E',
};

export const INTERIOR_SECTION_ORDER = ['warm-shell', 'ffe'];

// ============================================================================
// DEFAULT SCOPE OF WORK TEMPLATE
// ============================================================================
// Uses the same stage naming conventions as StagesPage.jsx (ids 1–16).
// Each stage has standard deliverables (included by default) and optional ones.

export const DEFAULT_SCOPE_TEMPLATE = {
  id: 'brio-standard-v1',
  name: 'BRIO Standard Scope of Work',
  version: 1,
  templateIntroduction: 'Note - This Scope of Work is intended as a general reference framework outlining the typical responsibilities, deliverables, and processes followed by the Design Consultant. It is not an exhaustive or project-specific commitment. Actual deliverables, level of detail, timelines, and any additional services may vary depending on the unique requirements, scale, and complexity of each project. All specific obligations and outputs will be confirmed and agreed upon separately for each commission or project.',
  interiorDeliverables: [
    { id: 'intd-1', label: 'Space planning', included: true, section: 'warm-shell' },
    { id: 'intd-2', label: 'Furniture Layouts', included: true, section: 'warm-shell' },
    { id: 'intd-3', label: 'Material palette and mood boards', included: true, section: 'warm-shell' },
    { id: 'intd-4', label: 'Reference Images', included: true, section: 'warm-shell' },
    { id: 'intd-5', label: 'Selection of floor finishes', included: true, section: 'warm-shell' },
    { id: 'intd-6', label: 'Selection of wall finishes', included: true, section: 'warm-shell' },
    { id: 'intd-7', label: 'Furniture selection and/or design (Shop drawings excluded: payable as additional scope)', included: true, section: 'warm-shell' },
    { id: 'intd-8', label: 'Selection of decorative hardware (handles, knobs, bolts, latches, door stoppers, hinges etc.)', included: true, section: 'warm-shell' },
    { id: 'intd-9', label: 'Selection of Decorative light fittings (hanging lamps, floor lamps, wall lights, landscape lights etc.)', included: true, section: 'warm-shell' },
    { id: 'intd-10', label: 'Selection of bathroom fixtures, fittings and accessories etc.', included: true, section: 'warm-shell' },
    { id: 'intd-11', label: 'Selection of window shading (blinds, curtains, screens etc.)', included: true, section: 'warm-shell' },
    { id: 'intd-12', label: 'Selection of switches and sockets', included: true, section: 'warm-shell' },
    { id: 'intd-13', label: 'Selection of white goods (hob, microwave, oven, dishwasher, TV, Music system etc.), if applicable', included: true, section: 'warm-shell' },
    { id: 'intd-14', label: 'Design of built-in furniture (wardrobes, kitchen cabinets, storage cupboards) and selection of veneers / wood for the same', included: true, section: 'warm-shell' },
    { id: 'intd-15', label: 'Selection and integration of Art and Sculptures', included: true, section: 'warm-shell' },
    { id: 'intd-16', label: 'Selection of Decor accessories', included: true, section: 'warm-shell' },
    { id: 'intd-17', label: 'Integration with building design, structure and MEP services', included: true, section: 'warm-shell' },
  ],
  stages: [
    {
      key: 'pcd',
      stageId: 17,
      name: 'PCD',
      fullName: 'Preconcept / Masterplan',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'pcd-1', label: 'Masterplan drawings' },
        { id: 'pcd-2', label: 'Massing studies' },
        { id: 'pcd-3', label: 'Site context analysis' },
        { id: 'pcd-4', label: 'Design vision statement' },
      ],
      optionalDeliverables: [
        { id: 'pcd-opt-1', label: 'Precedent study compilation' },
        { id: 'pcd-opt-2', label: 'Urban design framework' },
      ],
      notes: '',
    },
    {
      key: 'cd-ad',
      stageId: 2,
      name: 'CD AD',
      fullName: 'Concept Design — Architecture',
      description: '',
      introduction: '1. Concept Design Phase\nTo translate the client\u2019s vision and the project brief into a design concept.\n\n**Programming**\nThe Design Consultant shall provide {{programming-meetings}} meeting(s) to discuss the Client\u2019s goals and establish the project requirements including the Project Scope (see description below), the Client\u2019s budget expectations, space requirements and aesthetic preferences.\nProject Scope \u2013 all elements of the Project to be designed or specified by the Design Consultant that will have an associated construction cost.\n\nThe client will provide information to the Design Consultant such as a detailed topographical survey, local building regulations and any other relevant site related information. Client to appoint a Liasioning Consultant if applicable at the pre-concept stage.\n\n**Pre-Concept Design**\nBased on the requirements established at the Programming meeting, the Design Consultant shall visit the site and prepare a Pre-Concept Design comprised of up to {{sketch-options}} proposed sketch design options for the Project. Each option will include sketch plans and preliminary massing studies as required to illustrate the design intent. The Design Consultant shall provide {{preconcept-meetings}} meeting(s) to present and discuss the Pre-Concept Design solutions along with a preliminary area statement.\n\n**Final Concept Design**\nThe Client along with the Design Consultant shall arrive at {{final-concept-options}} preferred solution after discussing changes required to better meet the project requirements. Design Consultant shall proceed to take this option to Final Concept Design where the layouts will get better defined and basic 3D views will be prepared to understand the spatiality.\nThe Design Consultant shall provide {{final-concept-meetings}} meeting(s) to present the Final Concept Design.\nFinal Concept Design Deliverables will be as follows:',
      standardDeliverables: [
        { id: 'cdad-1', label: 'Programming meetings', quantity: '1-2', quantityToken: 'programming-meetings' },
        { id: 'cdad-2', label: 'Sketch design options', quantity: '2-3', quantityToken: 'sketch-options' },
        { id: 'cdad-3', label: 'Pre Concept Presentation meeting', quantity: '1', quantityToken: 'preconcept-meetings' },
        { id: 'cdad-4', label: 'Final Concept design Option', quantity: '1', quantityToken: 'final-concept-options' },
        { id: 'cdad-5', label: 'Final Concept Presentation meeting', quantity: '1-2', quantityToken: 'final-concept-meetings' },
        { id: 'cdad-6', label: 'Site analysis and documentation' },
        { id: 'cdad-7', label: 'Layouts of all floors' },
        { id: 'cdad-8', label: 'Preliminary sketch 3D views' },
        { id: 'cdad-9', label: 'Reference images to indicate design intent' },
        { id: 'cdad-10', label: 'Programmatic area statement' },
      ],
      optionalDeliverables: [
        { id: 'cdad-opt-1', label: 'Physical scale model' },
        { id: 'cdad-opt-2', label: 'Sustainability strategy outline' },
      ],
      notesRevisions: '1',
      notesMeetings: '2',
      notes: 'The Design Consultant shall prepare a maximum of {{revisions}} revision(s) to the selected Concept Design solution and provide up to {{meetings}} meeting(s) to present the designs and revisions. Any additional revisions during the Concept Design Phase shall be considered Additional Services.\nThe Concept Design drawings and Construction Budget shall be approved by the Client prior to proceeding to the next phase.',
    },
    {
      key: 'cd-id',
      stageId: 3,
      name: 'CD ID',
      fullName: 'Concept Design — Interior',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'cdid-1', label: 'Pre Concept Presentation meeting', quantity: '1', quantityToken: 'preconcept-meetings' },
        { id: 'cdid-2', label: 'Sketch design options', quantity: '2-3', quantityToken: 'sketch-options' },
        { id: 'cdid-3', label: 'Programming meetings', quantity: '1-2', quantityToken: 'programming-meetings' },
        { id: 'cdid-4', label: 'Final Concept design Option', quantity: '1', quantityToken: 'final-concept-options' },
        { id: 'cdid-5', label: 'Final Concept Presentation meeting', quantity: '1-2', quantityToken: 'final-concept-meetings' },
        { id: 'cdid-6', label: 'Interior concept boards / mood boards' },
        { id: 'cdid-7', label: 'Preliminary interior layouts' },
        { id: 'cdid-8', label: 'Material and finish selections (initial)' },
      ],
      optionalDeliverables: [
        { id: 'cdid-opt-1', label: 'Furniture concept proposal' },
        { id: 'cdid-opt-2', label: 'Lighting concept outline' },
      ],
      notesRevisions: '1',
      notesMeetings: '2',
      notes: 'The Design Consultant shall prepare a maximum of {{revisions}} revision(s) to the selected Concept Design solution and provide up to {{meetings}} meeting(s) to present the designs and revisions. Any additional revisions during the Concept Design Phase shall be considered Additional Services.\nThe Concept Design drawings and Construction Budget shall be approved by the Client prior to proceeding to the next phase.',
    },
    {
      key: 'sd-ad',
      stageId: 4,
      name: 'SD AD',
      fullName: 'Schematic Design — Architecture',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'sdad-1', label: 'Schematic design drawings (plans, sections, elevations)' },
        { id: 'sdad-2', label: 'Structural system outline' },
        { id: 'sdad-3', label: '3D rendered exterior/interior views', quantity: '6', quantityToken: 'sdad-3d-views' },
        { id: 'sdad-4', label: 'Building systems overview (MEP)' },
        { id: 'sdad-5', label: 'Updated cost estimate' },
      ],
      optionalDeliverables: [
        { id: 'sdad-opt-1', label: 'Energy performance modelling' },
        { id: 'sdad-opt-2', label: 'Landscape schematic design' },
      ],
      notesRevisions: '1',
      notesMeetings: '2',
      notes: 'The Design Consultant shall prepare a maximum of {{revisions}} revision(s) to the selected Schematic Design solution and provide up to {{meetings}} meeting(s) to present the designs and revisions. Any additional revisions during the Schematic Design Phase shall be considered Additional Services.\nThe Schematic Design drawings and Construction Budget shall be approved by the Client prior to proceeding to the next phase.',
    },
    {
      key: 'sd-id',
      stageId: 5,
      name: 'SD ID',
      fullName: 'Schematic Design — Interior',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'sdid-1', label: 'Detailed interior layout plans' },
        { id: 'sdid-2', label: 'Material specification sheets' },
        { id: 'sdid-3', label: '3D rendered exterior/interior views (limited to renders)', quantity: '8', quantityToken: 'sdid-3d-views' },
        { id: 'sdid-4', label: 'Reflected ceiling plans' },
      ],
      optionalDeliverables: [
        { id: 'sdid-opt-1', label: 'Custom furniture design drawings' },
        { id: 'sdid-opt-2', label: 'Signage and wayfinding concept' },
      ],
      notesRevisions: '1',
      notesMeetings: '2',
      notes: 'The Design Consultant shall prepare a maximum of {{revisions}} revision(s) to the selected Schematic Design solution and provide up to {{meetings}} meeting(s) to present the designs and revisions. Any additional revisions during the Schematic Design Phase shall be considered Additional Services.\nThe Schematic Design drawings and Construction Budget shall be approved by the Client prior to proceeding to the next phase.',
    },
    {
      key: 'dd-ad',
      stageId: 6,
      name: 'DD AD',
      fullName: 'Design Development — Architecture',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'ddad-1', label: 'Design development drawings (all disciplines)' },
        { id: 'ddad-2', label: 'Technical specifications' },
        { id: 'ddad-3', label: '3D rendered exterior/interior views', quantity: '6', quantityToken: 'ddad-3d-views' },
        { id: 'ddad-4', label: 'Consultant coordination package' },
        { id: 'ddad-5', label: 'Detailed cost estimate' },
      ],
      optionalDeliverables: [
        { id: 'ddad-opt-1', label: 'Building permit application support' },
        { id: 'ddad-opt-2', label: 'Value engineering review' },
      ],
      notesRevisions: '1',
      notesMeetings: '2',
      notes: 'The Design Consultant shall prepare a maximum of {{revisions}} revision(s) to the selected Design Development solution and provide up to {{meetings}} meeting(s) to present the designs and revisions. Any additional revisions during the Design Development Phase shall be considered Additional Services.\nThe Design Development drawings and Construction Budget shall be approved by the Client prior to proceeding to the next phase.',
    },
    {
      key: 'dd-id',
      stageId: 7,
      name: 'DD ID',
      fullName: 'Design Development — Interior',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'ddid-1', label: 'Detailed interior drawings and sections' },
        { id: 'ddid-2', label: '3D rendered interior views', quantity: '8', quantityToken: 'ddid-3d-views' },
        { id: 'ddid-3', label: 'Joinery and millwork details' },
        { id: 'ddid-4', label: 'Fixture and fitting schedules' },
      ],
      optionalDeliverables: [
        { id: 'ddid-opt-1', label: 'Art and accessories specification' },
      ],
      notesRevisions: '1',
      notesMeetings: '2',
      notes: 'The Design Consultant shall prepare a maximum of {{revisions}} revision(s) to the selected Design Development solution and provide up to {{meetings}} meeting(s) to present the designs and revisions. Any additional revisions during the Design Development Phase shall be considered Additional Services.\nThe Design Development drawings and Construction Budget shall be approved by the Client prior to proceeding to the next phase.',
    },
    {
      key: 'aoc-ad',
      stageId: 8,
      name: 'AoC AD',
      fullName: 'Awarding of Contract — Architecture',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'aocad-1', label: 'Tender drawings package' },
        { id: 'aocad-2', label: 'Bill of quantities (architectural)' },
        { id: 'aocad-3', label: 'Tender evaluation support' },
      ],
      optionalDeliverables: [
        { id: 'aocad-opt-1', label: 'Pre-qualification assessment' },
      ],
      notes: '',
    },
    {
      key: 'aoc-id',
      stageId: 9,
      name: 'AoC ID',
      fullName: 'Awarding of Contract — Interior',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'aocid-1', label: 'Interior tender drawings' },
        { id: 'aocid-2', label: 'Interior bill of quantities' },
        { id: 'aocid-3', label: 'Vendor / supplier coordination' },
      ],
      optionalDeliverables: [
        { id: 'aocid-opt-1', label: 'Mock-up specifications' },
      ],
      notes: '',
    },
    {
      key: 'td-ad',
      stageId: 10,
      name: 'TD AD',
      fullName: 'Tender Design — Architecture',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'tdad-1', label: 'Tender-level architectural drawings' },
        { id: 'tdad-2', label: 'Technical specifications for tender' },
        { id: 'tdad-3', label: 'Scope clarification documents' },
      ],
      optionalDeliverables: [
        { id: 'tdad-opt-1', label: 'Alternate design options for tender' },
      ],
      notes: '',
    },
    {
      key: 'td-id',
      stageId: 11,
      name: 'TD ID',
      fullName: 'Tender Design — Interior',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'tdid-1', label: 'Tender-level interior drawings' },
        { id: 'tdid-2', label: 'Interior specifications for tender' },
      ],
      optionalDeliverables: [
        { id: 'tdid-opt-1', label: 'Sample and mock-up requirements' },
      ],
      notes: '',
    },
    {
      key: 'gfc-1',
      stageId: 12,
      name: 'GFC 1',
      fullName: 'Good for Construction 1',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'gfc1-1', label: 'GFC drawings — structure and envelope' },
        { id: 'gfc1-2', label: 'Construction specifications (phase 1)' },
        { id: 'gfc1-3', label: 'Site coordination drawings' },
      ],
      optionalDeliverables: [
        { id: 'gfc1-opt-1', label: 'Construction phasing plan' },
      ],
      notes: '',
    },
    {
      key: 'gfc-2',
      stageId: 13,
      name: 'GFC 2',
      fullName: 'Good for Construction 2',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'gfc2-1', label: 'GFC drawings — interior fit-out' },
        { id: 'gfc2-2', label: 'MEP coordination drawings' },
        { id: 'gfc2-3', label: 'Finishing schedule' },
      ],
      optionalDeliverables: [],
      notes: '',
    },
    {
      key: 'gfc-3',
      stageId: 14,
      name: 'GFC 3',
      fullName: 'Good for Construction 3',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'gfc3-1', label: 'Final GFC drawings and revisions' },
        { id: 'gfc3-2', label: 'As-built documentation coordination' },
      ],
      optionalDeliverables: [],
      notes: '',
    },
    {
      key: 'completion',
      stageId: 15,
      name: 'Completion',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'comp-1', label: 'Project handover report' },
        { id: 'comp-2', label: 'Defects list and rectification tracking' },
        { id: 'comp-3', label: 'Final project photography' },
        { id: 'comp-4', label: 'As-built drawing set (final)' },
      ],
      optionalDeliverables: [
        { id: 'comp-opt-1', label: 'Post-occupancy evaluation' },
        { id: 'comp-opt-2', label: 'Maintenance manual compilation' },
      ],
      notes: '',
    },
    {
      key: 'cc',
      stageId: 16,
      name: 'CC',
      fullName: 'Construction Coordination',
      description: '',
      introduction: '',
      standardDeliverables: [
        { id: 'cc-1', label: 'Regular site visit reports' },
        { id: 'cc-2', label: 'RFI responses log' },
        { id: 'cc-3', label: 'Shop drawing reviews' },
        { id: 'cc-4', label: 'Progress meeting minutes' },
      ],
      optionalDeliverables: [
        { id: 'cc-opt-1', label: 'Change order assessment support' },
      ],
      notes: '',
    },
  ],
};


// ============================================================================
// QUANTITY TOKEN RESOLUTION
// ============================================================================
// Replaces {{token}} placeholders in introduction/notes text with quantity
// values from the deliverables that define quantityToken fields.

export function resolveQuantityTokens(text, deliverables) {
  if (!text) return text;
  const tokenMap = {};
  deliverables.forEach(d => {
    if (d.quantityToken && d.quantity) {
      tokenMap[d.quantityToken] = d.quantity;
    }
  });
  return text.replace(/\{\{([^}]+)\}\}/g, (match, token) => {
    const trimmed = token.trim();
    return tokenMap[trimmed] !== undefined ? tokenMap[trimmed] : match;
  });
}


// ============================================================================
// NOTES TOKEN RESOLUTION
// ============================================================================
// Replaces {{revisions}} and {{meetings}} placeholders in notes text with
// stage-level notesRevisions / notesMeetings values.

export function resolveNotesTokens(text, stage) {
  if (!text) return text;
  let resolved = text;
  if (stage.notesRevisions !== undefined) {
    resolved = resolved.replace(/\{\{revisions\}\}/g, stage.notesRevisions);
  }
  if (stage.notesMeetings !== undefined) {
    resolved = resolved.replace(/\{\{meetings\}\}/g, stage.notesMeetings);
  }
  return resolved;
}


// ============================================================================
// SCOPE STATUS CONSTANTS
// ============================================================================

export const SCOPE_STATUSES = {
  DRAFT: 'draft',
  NEGOTIATION: 'negotiation',
  CONFIRMED: 'confirmed',
  ARCHIVED: 'archived',
};

export const SCOPE_STATUS_LABELS = {
  draft: 'Draft',
  negotiation: 'Negotiation',
  confirmed: 'Confirmed',
  archived: 'Archived',
};

export const SCOPE_STATUS_BADGE_VARIANT = {
  draft: 'neutral',
  negotiation: 'caution',
  confirmed: 'success',
  archived: 'info',
};


// ============================================================================
// FACTORY: Create a new contract scope record from the template
// ============================================================================

let _nextScopeId = Date.now();

export function createContractScope(projectIdOrOptions, title, template = DEFAULT_SCOPE_TEMPLATE) {
  const isOptions = projectIdOrOptions !== null && typeof projectIdOrOptions === 'object';
  const projectId = isOptions ? (projectIdOrOptions.projectId ?? null) : projectIdOrOptions;
  const inquiryId = isOptions ? (projectIdOrOptions.inquiryId ?? null) : null;
  const now = new Date().toISOString();
  return {
    id: `scope-${++_nextScopeId}`,
    projectId,
    inquiryId,
    title: title || 'Scope of Work',
    templateId: template.id,
    version: 1,
    status: SCOPE_STATUSES.DRAFT,
    isActiveVersion: true,
    templateIntroduction: template.templateIntroduction || '',
    // Section-level toggles for the Summary of Interior Deliverables.
    // A scope can include Warm Shell, FF&E, or both; if both are off,
    // the entire summary is omitted from the PDF.
    interiorWarmShellSelected: false,
    interiorFfeSelected: false,
    // Editable verbatim text shown above the Summary of Interior
    // Deliverables list, mirroring how each stage carries its own
    // `introduction`. Populated from the template if provided, blank
    // for a fresh scope, and seeded by the import wizard from any
    // prose lines detected in the PDF's interior section.
    interiorIntroduction: template.interiorIntroduction || '',
    interiorDeliverables: (template.interiorDeliverables || []).map(d => ({
      ...d,
      section: d.section || INTERIOR_SECTIONS.WARM_SHELL,
    })),
    stages: template.stages.map(s => ({
      key: s.key,
      stageId: s.stageId,
      name: s.name,
      fullName: s.fullName || s.name,
      selected: false,
      descriptionReviewed: false,
      description: s.description,
      introduction: s.introduction || '',
      notes: s.notes || '',
      ...(s.notesRevisions !== undefined ? { notesRevisions: s.notesRevisions } : {}),
      ...(s.notesMeetings !== undefined ? { notesMeetings: s.notesMeetings } : {}),
      deliverables: {
        standard: s.standardDeliverables.map(d => ({
          ...d,
          type: 'standard',
          included: true,
        })),
        optional: s.optionalDeliverables.map(d => ({
          ...d,
          type: 'optional',
          included: false,
        })),
        custom: [],
      },
    })),
    notes: '',
    negotiationNotes: '',
    createdAt: now,
    updatedAt: now,
    confirmedAt: null,
    confirmedBy: null,
  };
}


// ============================================================================
// MIGRATION: Normalise legacy scopes loaded from storage
// ============================================================================
// Older scopes used a single `interiorDeliverablesSelected` boolean and items
// without a `section` field. This helper backfills the new shape:
//   - missing section → 'warm-shell'
//   - missing warm/ffe toggles → both true if the legacy toggle was on,
//     otherwise both false
// Callers should run this once per loaded scope; it returns the scope
// unchanged if it already conforms.

export function normalizeInteriorSections(scope) {
  if (!scope) return scope;
  const items = Array.isArray(scope.interiorDeliverables) ? scope.interiorDeliverables : [];
  const needsItemMigration = items.some(d => !d.section);
  const hasNewToggles =
    scope.interiorWarmShellSelected !== undefined ||
    scope.interiorFfeSelected !== undefined;
  if (!needsItemMigration && hasNewToggles) return scope;

  const migratedItems = items.map(d =>
    d.section ? d : { ...d, section: INTERIOR_SECTIONS.WARM_SHELL }
  );
  const legacyOn = !!scope.interiorDeliverablesSelected;
  return {
    ...scope,
    interiorWarmShellSelected:
      scope.interiorWarmShellSelected !== undefined
        ? scope.interiorWarmShellSelected
        : legacyOn,
    interiorFfeSelected:
      scope.interiorFfeSelected !== undefined
        ? scope.interiorFfeSelected
        : legacyOn,
    interiorDeliverables: migratedItems,
  };
}

// Convenience: derive the legacy boolean from the two new toggles. Used by
// downstream renderers / wizards that still read the old field.
export function isInteriorSummarySelected(scope) {
  if (!scope) return false;
  if (scope.interiorWarmShellSelected || scope.interiorFfeSelected) return true;
  // Fall back to legacy flag for un-migrated scopes still in flight.
  return !!scope.interiorDeliverablesSelected;
}


// ============================================================================
// VALIDATION: Check if scope is ready for confirmation
// ============================================================================

export function validateScopeForConfirmation(scope) {
  const errors = [];

  const selectedStages = scope.stages.filter(s => s.selected);
  if (selectedStages.length === 0) {
    errors.push('At least one stage must be selected.');
  }

  selectedStages.forEach(stage => {
    if (!stage.descriptionReviewed) {
      errors.push(`Stage "${stage.name}" description has not been reviewed.`);
    }
    const allDeliverables = [
      ...stage.deliverables.standard,
      ...stage.deliverables.optional,
      ...stage.deliverables.custom,
    ];
    if (allDeliverables.length === 0) {
      errors.push(`Stage "${stage.name}" has no deliverables.`);
    }
  });

  if (scope.status === SCOPE_STATUSES.DRAFT) {
    errors.push('Scope must move past draft status before confirmation.');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}


// ============================================================================
// DOWNSTREAM INTEGRATION SELECTORS
// ============================================================================

export function getConfirmedScopeForProject(scopes, projectId) {
  return scopes.find(
    s => s.projectId === projectId && s.status === SCOPE_STATUSES.CONFIRMED && s.isActiveVersion
  ) || null;
}

export function getConfirmedStagesForProject(scopes, projectId) {
  const scope = getConfirmedScopeForProject(scopes, projectId);
  if (!scope) return [];
  return scope.stages.filter(s => s.selected);
}

export function getConfirmedDeliverablesForProject(scopes, projectId) {
  const stages = getConfirmedStagesForProject(scopes, projectId);
  return stages.flatMap(stage => {
    const included = [
      ...stage.deliverables.standard.filter(d => d.included),
      ...stage.deliverables.optional.filter(d => d.included),
      ...stage.deliverables.custom,
    ];
    return included.map(d => ({
      ...d,
      stageKey: stage.key,
      stageName: stage.name,
    }));
  });
}

export function getNextStageScope(scopes, projectId, currentStageKey) {
  const stages = getConfirmedStagesForProject(scopes, projectId);
  const idx = stages.findIndex(s => s.key === currentStageKey);
  if (idx === -1 || idx >= stages.length - 1) return null;
  return stages[idx + 1];
}

export function getCompletedStageScope(scopes, projectId, stageKey) {
  const stages = getConfirmedStagesForProject(scopes, projectId);
  return stages.find(s => s.key === stageKey) || null;
}

export function getScopesForInquiry(scopes, inquiryId) {
  return scopes.filter(
    s => s.inquiryId != null && String(s.inquiryId) === String(inquiryId)
  );
}

export function isInquiryScope(scope) {
  return scope != null && scope.inquiryId != null && scope.projectId == null;
}


// ============================================================================
// REVISION: Create a new version from a confirmed scope
// ============================================================================

export function createRevisionFromScope(confirmedScope) {
  const now = new Date().toISOString();
  return {
    ...confirmedScope,
    id: `scope-${++_nextScopeId}`,
    version: confirmedScope.version + 1,
    status: SCOPE_STATUSES.DRAFT,
    isActiveVersion: false, // becomes active only on confirmation
    notes: '',
    negotiationNotes: '',
    createdAt: now,
    updatedAt: now,
    confirmedAt: null,
    confirmedBy: null,
  };
}
